// src/components/MyStore/CreateLotModal.js
// I-02d Slice 4 — create a sellable lot on a variation. I-02d.3: ONE
// modal offering all three types — Pair (exactly 2), Group (2+, exact
// animals) and Bundle (quantity drawn from the stock pool) — on any
// variation, because lots are additive.
// Picker feed = lot-eligible-animals (same-species animals not deceased/
// sold/on-hold/held-back/breeder/in another active lot). LISTED animals
// ARE offered: creating the lot removes them from their listing.
// Backend 400s are operator-quality — surface verbatim.
import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../ui/Modal';
import Field from '../ui/Field';
import CheckField from '../ui/CheckField';
import { createVariationLot, getLotEligibleAnimals } from '../../services/api';
// Prod hotfix 2026-10-05 — the ONE animal identity (handle, sex word,
// species/morph, where it lives), shared with the racks drawer.
import { animalHandle, animalIdentity } from '../Facilities/animalRowHelpers';
import { sexBasisQualifier } from '../../constants/enums';

export const GENDER_GLYPH = { 0: '♂', 1: '♀', 2: '?' };

// Cowork re-run F1 — ONE display rule for lot-member identity, shared by
// the operator chips (this modal, LotsPanel) AND the buyer-facing
// surfaces (LotListingCard, cart, checkout, confirmation): the animal's
// name when present, else #<FIRST6-of-id uppercase> so a buyer always
// knows WHICH animals a lot contains.
//
// Prod hotfix 2026-10-05: the rule now lives in ONE place,
// `animalRowHelpers.animalHandle`, which the racks drawer reads too.
export const memberDisplayName = (m) => animalHandle(m);

// I-02d.2 member-display tweak (operator-approved): on BUYER-facing
// rosters (LotListingCard, carousel captions, cart/checkout/confirmation)
// an unnamed member renders `#08DC45 · ~3.4 yr` — short id + approx age
// (the sex glyph is rendered separately at every call site). Named
// animals are unchanged: name only. Falls back to memberDisplayName when
// the member has no out_of_water_date.
//
// Q-sex-certainty: a temp-sexed or probable member says so after its
// label — "Pip (temp-sexed)", "#08DC45 · ~3 mo (probable)" — so a roster
// never states a sex more firmly than the record (the email roster's
// twin is `services/sex_basis.roster_qualifier`).
const isSexedGender = (g) => g === 0 || g === 1 || g === '0' || g === '1';
export const memberDisplayLabel = (m) => {
  const q = isSexedGender(m?.gender) ? sexBasisQualifier(m?.sex_basis) : '';
  const tail = q ? ` (${q})` : '';
  if (m?.name) return `${m.name}${tail}`;
  const short = memberDisplayName(m);
  const age = approxAge(m?.out_of_water_date);
  return `${age ? `${short} · ${age}` : short}${tail}`;
};

// Rough age from out_of_water_date (frogs' post-morph clock). No shared
// helper exists in utils/ — keep this local and approximate on purpose.
export const approxAge = (isoDate) => {
  if (!isoDate) return null;
  const then = new Date(`${isoDate}T00:00:00`);
  if (isNaN(then.getTime())) return null;
  const months = (Date.now() - then.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
  if (months < 0) return null;
  if (months < 1) return '<1 mo';
  if (months < 24) return `~${Math.round(months)} mo`;
  return `~${(months / 12).toFixed(1)} yr`;
};

// Backend error bodies: {error: str} OR {errors: [{id, error}]} OR
// {error: str, errors: [...]} (re-activate). Flatten to one string.
export const lotErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  const parts = [];
  if (data.error) parts.push(String(data.error));
  if (Array.isArray(data.errors)) {
    data.errors.forEach((e) => parts.push(String(e?.error || e)));
  }
  return parts.length ? parts.join(' ') : fallback;
};

const CreateLotModal = ({ isOpen, onClose, variation, onCreated, poolAvailable = null }) => {
  const [lotType, setLotType] = useState('pair');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('2'); // pool mode only
  const [selectedIds, setSelectedIds] = useState([]); // ordered animal ids
  const [eligible, setEligible] = useState(null); // null = loading
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isPool = lotType === 'pool_group';
  const variationId = variation?.id;
  // adv-review S2 — a bundle draws from the STOCK pool, and animals linked
  // to an individual-mode listing are excluded from stock auto-selection.
  // The pool on such a listing is therefore permanently 0: the bundle would
  // be created, derive 'pool_short' forever and never sell. Offering the
  // radio here was a trap; the backend now 400s on it too.
  const isIndividualMode = (variation?.sales_mode || 'stock') === 'individual';

  const loadEligible = useCallback(async () => {
    setEligible(null);
    try {
      const data = await getLotEligibleAnimals(variationId);
      setEligible(data.animals || []);
    } catch (err) {
      setEligible([]);
      setFormError(lotErrorMessage(err, 'Could not load eligible animals'));
    }
  }, [variationId]);

  // Fresh state per open — a stale picker offers animals that joined
  // another lot since the last open. Pool mode never fetches the picker
  // feed (there is nothing to pick).
  useEffect(() => {
    if (!isOpen || !variationId) return;
    setLotType('pair');
    setTitle('');
    setPrice('');
    setQuantity('2');
    setSelectedIds([]);
    setFormError('');
    setSubmitting(false);
    loadEligible();
  }, [isOpen, variationId, loadEligible]);

  const maxForType = lotType === 'pair' ? 2 : Infinity;

  const toggleAnimal = (animalId) => {
    setSelectedIds((prev) => {
      if (prev.includes(animalId)) return prev.filter((i) => i !== animalId);
      if (prev.length >= maxForType) return prev; // pair: hard cap at 2
      return [...prev, animalId];
    });
  };

  const handleTypeChange = (next) => {
    setLotType(next);
    setFormError('');
    // Shrinking group → pair with 3+ picked would silently submit-fail;
    // trim to the first 2 so the count indicator stays truthful.
    if (next === 'pair') setSelectedIds((prev) => prev.slice(0, 2));
    // A bundle has no pinned animals at all — the backend 400s on
    // animal_ids, so drop the picks rather than sending them.
    if (next === 'pool_group') setSelectedIds([]);
  };

  const countLabel = lotType === 'pair'
    ? `${selectedIds.length} of 2 selected`
    : `${selectedIds.length} selected (minimum 2)`;
  const countOk = lotType === 'pair' ? selectedIds.length === 2 : selectedIds.length >= 2;

  const quantityOk = quantity !== ''
    && Number.isInteger(Number(quantity)) && Number(quantity) >= 2;

  const canSubmit = !submitting
    && title.trim().length > 0
    && price !== '' && !isNaN(Number(price)) && Number(price) >= 0
    && (isPool ? quantityOk : countOk);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setFormError('');
    setSubmitting(true);
    try {
      const lot = await createVariationLot(variationId, isPool
        ? {
          lotType: 'pool_group',
          title: title.trim(),
          price,
          quantity: Number(quantity),
        }
        : {
          lotType,
          title: title.trim(),
          price,
          animalIds: selectedIds,
        });
      if (onCreated) onCreated(lot);
      onClose();
    } catch (err) {
      setFormError(lotErrorMessage(err, isPool ? 'Failed to create bundle' : 'Failed to create lot'));
      setSubmitting(false);
    }
  };

  // UI leftovers C2 (2026-11-03): a ui/Modal `form`. Create lot / Create
  // bundle is the one primary (the variant draws Cancel beside it and
  // shuts every way out while it is busy); a backend refusal is the
  // variant's refusal — the server's own sentence, in red above the
  // buttons. The fields are ui/Field; the request is what it was.
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New lot or bundle"
      variant="form"
      refusal={formError || null}
      primaryAction={{
        label: isPool ? 'Create bundle' : 'Create lot',
        onClick: handleSubmit,
        disabled: !canSubmit,
        busy: submitting,
        busyLabel: 'Creating…',
      }}
      onCancel={onClose}
    >

      <div style={{ marginBottom: 'var(--ff-space-md, 12px)' }}>
        <div style={{ display: 'flex', gap: 'var(--ff-space-xl, 24px)', flexWrap: 'wrap' }}>
          {[
            ['pair', 'Pair (exactly 2)'],
            ['group', 'Group (2 or more)'],
            ['pool_group', 'Bundle (quantity from stock)'],
          ]
            // adv-review S2: no Bundle option on an individual-mode listing.
            .filter(([value]) => !(value === 'pool_group' && isIndividualMode))
            .map(([value, label]) => (
              <CheckField
                key={value}
                type="radio"
                name="ff-lot-type"
                label={label}
                checked={lotType === value}
                onChange={() => handleTypeChange(value)}
              />
            ))}
        </div>
        {isIndividualMode && (
          <div style={{ marginTop: 'var(--ff-space-xs, 4px)', color: 'var(--ff-text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
            Bundles draw from stock — this listing sells animals
            individually, so its stock pool is always empty. Use a Pair or
            Group to sell several of these animals as one unit.
          </div>
        )}
        <div style={{ marginTop: 'var(--ff-space-xs, 4px)', color: 'var(--ff-text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
          {lotType === 'pair'
            ? 'Any two animals — the lot badge auto-upgrades to “Sexed Pair” when the members are one male + one female, both sexes confirmed (a temp-sexed or probable member keeps it “Pair”).'
            : lotType === 'group'
              ? 'Specific animals sold as one unit.'
              : 'No specific animals — N are auto-selected from available stock at checkout.'}
        </div>
        <div style={{ marginTop: 'var(--ff-space-xs, 4px)', color: 'var(--ff-text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
          This lists ALONGSIDE whatever the listing already sells.
        </div>
      </div>

      {isPool && (
        <div style={{ marginBottom: 'var(--ff-space-md, 12px)' }}>
          <div style={{ color: 'var(--ff-text-secondary, #6b7280)', fontSize: '0.875rem', marginBottom: 'var(--ff-space-sm, 8px)' }}>
            A bundle of N animals at one bundle price, pulled from this
            listing’s available stock at checkout — no specific animals are
            reserved, and individuals stay buyable. The bundle auto-delists
            whenever fewer than N animals are available.
          </div>
          <Field
            label="Animals per bundle"
            help={poolAvailable != null ? `Pool now: ${poolAvailable} available` : null}
          >
            <input
              type="number"
              min="2"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              style={{ width: 90 }}
            />
          </Field>
        </div>
      )}

      <Field label="Title" style={{ marginBottom: 'var(--ff-space-md, 12px)' }}>
        <input
          type="text"
          maxLength={200}
          value={title}
          placeholder={isPool ? 'e.g. "Trio deal — Azureus"' : 'e.g. "Proven pair — Azureus"'}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>

      <Field
        label={isPool ? 'Bundle price' : 'Lot price'}
        htmlFor="ff-lot-price"
        style={{ marginBottom: 'var(--ff-space-md, 12px)' }}
        help={isPool
          ? 'one price for the whole bundle — the per-animal stock price does not apply'
          : 'one price for the whole lot — member price overrides are ignored'}
      >
        <span style={{ whiteSpace: 'nowrap' }}>
          $<input
            id="ff-lot-price"
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            style={{ width: 120 }}
          />
        </span>
      </Field>

      {!isPool && (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="ff-label">Animals</span>
        <span style={{
          fontWeight: 600,
          color: countOk ? 'var(--ff-semantic-success, #22c55e)' : 'var(--ff-text-secondary, #6b7280)',
        }}>
          {countLabel}
        </span>
      </div>
      <div style={{
        border: '1px solid var(--ff-border-light, #e5e7eb)',
        borderRadius: 'var(--ff-radius-md, 6px)',
        maxHeight: 280,
        overflowY: 'auto',
        marginTop: 'var(--ff-space-xs, 4px)',
      }}>
        {eligible === null ? (
          <div style={{ padding: 'var(--ff-space-md, 12px)' }} className="text-muted">
            Loading eligible animals…
          </div>
        ) : eligible.length === 0 ? (
          <div style={{ padding: 'var(--ff-space-md, 12px)' }} className="text-muted">
            No eligible animals — animals must be this species, not deceased,
            sold, on hold, held back, or flagged as breeder, and not already
            in an active lot.
          </div>
        ) : (
          eligible.map((a) => {
            const checked = selectedIds.includes(a.id);
            const capped = !checked && selectedIds.length >= maxForType;
            const age = approxAge(a.out_of_water_date);
            // Prod hotfix 2026-10-05 — "doesn't tell me what enclosure
            // they're in": every row says WHICH animal, its sex as a
            // word, what it is, and WHERE it lives ("in Tadpole 2E —
            // Rack 2 · 2.3", or "not in a tank").
            const who = animalIdentity(a);
            return (
              /* Cowork QA F8 — the row used to be ONE flex line holding
                 the checkbox, the ID and every tag. A long "listed in X —
                 will be removed from it" tag squeezed the flex items to
                 its left, so tagged and untagged rows put the checkbox
                 and the ID at different x positions and the columns
                 zig-zagged down the list. Now: checkbox is a fixed,
                 non-shrinking first column, and everything else lives in
                 ONE wrapping block whose first line is always
                 name/sex/age. Tags overflow onto a second line instead of
                 stealing width from the columns. */
              <label
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--ff-space-sm, 8px)',
                  padding: 'var(--ff-space-sm, 8px)',
                  borderBottom: '1px solid var(--ff-border-light, #e5e7eb)',
                  cursor: capped ? 'not-allowed' : 'pointer',
                  opacity: capped ? 0.5 : 1,
                  background: checked ? 'var(--ff-surface-secondary, #f8f9fa)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={capped}
                  onChange={() => toggleAnimal(a.id)}
                  style={{ flex: '0 0 auto', width: 'auto', marginTop: '3px' }}
                />
                <span
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'baseline',
                    columnGap: 'var(--ff-space-sm, 8px)',
                    rowGap: '2px',
                  }}
                >
                  <strong style={{ whiteSpace: 'nowrap' }}>{who.handle}</strong>
                  <span data-lot-sex={a.id} style={{ whiteSpace: 'nowrap' }}>{who.sex.label}</span>
                  {who.kind && <span data-lot-kind={a.id}>{who.kind}</span>}
                  {age && <span style={{ color: 'var(--ff-text-secondary, #6b7280)' }}>{age}</span>}
                  <span data-lot-where={a.id} style={{ flexBasis: '100%' }}>{who.where}</span>
                  {!a.has_own_photo && (
                    <span style={{ color: 'var(--ff-text-secondary, #6b7280)', fontSize: '0.8125rem' }}>
                      no photo
                    </span>
                  )}
                  {a.has_price_override && (
                    <span
                      style={{ color: 'var(--ff-semantic-warning-text, #111111)', fontSize: '0.8125rem' }}
                      title={`Individual price $${parseFloat(a.price_override).toFixed(2)} — ignored while in a lot`}
                    >
                      has individual price — ignored while in lot
                    </span>
                  )}
                  {a.listed_in?.length > 0 && (
                    <span
                      style={{ color: 'var(--ff-semantic-warning-text, #111111)', fontSize: '0.8125rem' }}
                      title="Creating this lot removes the animal from that listing"
                    >
                      listed in {a.listed_in.join(', ')} — will be removed from it
                    </span>
                  )}
                </span>
              </label>
            );
          })
        )}
      </div>
      </>
      )}
    </Modal>
  );
};

export default CreateLotModal;
